'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { RewardIcon } from '@workspace/ui';
import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { ApiCombobox, type ApiComboboxOption } from '@workspace/ui/components/custom/api-combobox';
import { Combobox, type ComboboxOption } from '@workspace/ui/components/custom/combobox';
import { DateRangePicker } from '@workspace/ui/components/custom/date-range-picker';
import {
  type ColumnInfiniteScrollTable,
  GroupedInfiniteScrollTable,
} from '@workspace/ui/components/custom/grouped-infinite-scroll-table';
import { TruncateWithTooltip } from '@workspace/ui/components/custom/truncate-with-tooltip';
import { Form, FormControl, FormField, FormItem } from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import {
  AvoidCollisionsContext,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@workspace/ui/components/popover';
import {
  formatAmount,
  formatTransferTypeLabel,
  TRANSFER_TYPE_LABELS,
  USER_ROLES,
  cn,
} from '@workspace/ui/lib';
import type { ApiResponse } from '@workspace/ui/types';
import { format, parseISO } from 'date-fns';
import {
  ArrowBigDownDash,
  ArrowBigUpDash,
  CheckCircle2,
  CircleAlert,
  CircleMinus,
  Clock,
  FileCog,
  ListFilter,
  ReceiptText,
  Search,
  WalletMinimal,
  X,
  XCircle,
} from 'lucide-react';
import Image from 'next/image';
import { useQueryStates } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { z } from 'zod';
import { getGigWorkerEarningSources } from '@/actions/gig-workers';
import { getPeople } from '@/actions/people';
import { useAuth } from '@/hooks/use-auth';
import { useIsContractor, useIsGigWorker } from '@/hooks/use-contractor';
import { useIsEmployee } from '@/hooks/use-employer';
import {
  type TransactionFilters,
  type TransactionMetadata,
  useInfiniteTransactions,
} from '@/hooks/use-transactions';
import { useUserVerification } from '@/hooks/use-user';
import { useTransferTypes } from '@/hooks/use-wallet';
import { isDefaultOrganisation } from '@/lib/default-organisation';
import fetchAPI from '@/lib/fetch-api';
import {
  clampTransactionsPageSize,
  transactionsTableParsers,
} from '@/lib/transactions-table-parsers';
import type { BankDetailsItem } from '@/types/bank';
import type { GigWorkerEarningSource } from '@/types/gig-workers';
import type { PersonResponse } from '@/types/people';

/** Transfer types where employer wallet transactions involve a team member (show recipient.name). */
const EMPLOYER_EMPLOYEE_RELATED_TRANSFER_TYPES = new Set([
  'payroll',
  'expense',
  'invoice',
  'team-transfer',
  'batch-transfer',
  'fee-reimbursement',
]);

const EMPLOYEE_WALLET_TRANSFER_TYPES = new Set([
  'payroll',
  'expense',
  'invoice',
  'bank-withdraw',
  'bank-withdrawal',
  'team-transfer',
  'currency-conversion',
  'payoneer',
  'internal-refund',
  'refund',
  'eps-card-top-up',
  'balance-reward',
  'rewards',
  'card-top-up',
  'wallet-top-up',
  'bank-top-up',
  'bank-transfer',
  'subscription',
  'other',
  'fee-reimbursement',
  'supplemental-pay',
]);

const GIG_PAY_TRANSFER_TYPES = new Set(['gig-payment', 'gig-payment-fee']);

const EARNING_SOURCE_ICON_CDN_BASE = 'https://i.nativeteams.com/assets/app/eor-app/onboarding';

const EXCLUDED_EARNING_SOURCE_KEYS = ['other', 'internal', 'not_applicable'];

function getEarningSourceIconUrl(key: string): string {
  return `${EARNING_SOURCE_ICON_CDN_BASE}/${key}.svg`;
}

export type TransactionStatus =
  | 'pending'
  | 'failed'
  | 'declined'
  | 'completed'
  | 'reserved'
  | 'cancelled'
  | 'refunded'
  | 'queued';

export interface Transaction {
  id: string;
  type: string;
  date: string;
  amount: string; // Keep as string for display (formatted with currency)
  balance: string;
  status: TransactionStatus;
  wallet_id: number;
  currency_id: number;
  reason: string;
  // Additional API fields
  numericId: number; // Original numeric ID from API
  currency: string;
  from_currency: string;
  to_currency: string;
  to_currency_id: number;
  created_at: string;
  description: string;
  provider: string;
  note: string | null;
  fees: {
    percent: number;
    value: number;
  };
  tax: number;
  tax_converted: number;
  user_name: string;
  user_email: string;
  user_id: number;
  numericAmount: number; // Original numeric amount from API
  converted_amount?: string;
  total_converted_amount: string;
  // New fields from actual API
  reference: string;
  date_of_request: string;
  date_of_payment: string | null;
  date_of_declined: string | null;
  date_of_rejection: string | null;
  bank_account?: string;
  bank_name?: string;
  bank_holder_name?: string;
  cardNumber?: string;
  epsCardNumber?: string;
  conversion_rate?: number;
  transfer_type?: string;
  is_incoming?: boolean;
  recipient_user_id?: number;
  recipient?: {
    id: number;
    name: string;
    email: string;
  };
  organisation_name?: string;
  numberOfRecipients?: number;
  attachmentFileName?: string;
  failedBatchTransferId?: number;
  metadata?: TransactionMetadata | null;
}

interface TransactionsInfinitePage {
  data: Transaction[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    has_more: boolean;
  };
}

function getTransactionDateKey(transaction: Transaction): string {
  const source = transaction.date_of_request || transaction.created_at;
  return format(parseISO(source), 'yyyy-MM-dd');
}

function formatTransactionGroupDate(dateKey: string): string {
  const todayKey = format(new Date(), 'yyyy-MM-dd');

  if (dateKey === todayKey) return 'Today';

  return format(parseISO(`${dateKey}T12:00:00`), 'dd MMMM yyyy');
}

function getTransactionGroupLabel(
  transaction: Transaction,
  previousTransaction: Transaction | null
): string | null {
  const dateKey = getTransactionDateKey(transaction);

  if (!previousTransaction) {
    return formatTransactionGroupDate(dateKey);
  }

  const previousDateKey = getTransactionDateKey(previousTransaction);
  if (dateKey === previousDateKey) {
    return null;
  }

  return formatTransactionGroupDate(dateKey);
}

function TransactionDateGroupHeader({ dateLabel }: { dateLabel: string }) {
  return (
    <div className="flex w-full flex-col">
      <p className="text-grey-500 dark:text-grey-400 px-1 pb-2 text-xs leading-3 font-medium">
        {dateLabel}
      </p>
      <div className="border-grey-100 dark:border-grey-800 border-t" />
    </div>
  );
}

// Currency interface from use-transactions.ts
interface Currency {
  id: number;
  name: string;
  code: string;
  flag_code: string;
  symbol: string;
  billing_currency: boolean;
}

// API Transaction interface from use-transactions.ts
interface ApiTransaction {
  id: number;
  type: string;
  status: string;
  amount: number;
  currency: string;
  balance: number;
  description: string;
  note?: string; // Optional field
  provider: string;
  fees: {
    percent: number;
    value: number;
  };
  currency_id: number;
  wallet_id: number;
  user_name: string;
  user_id: number;
  user_email: string;
  reference: string;
  date_of_request: string;
  date_of_payment: string | null;
  date_of_declined: string | null;
  date_of_rejection: string | null;
  converted_amount: string;
  fee: string;
  tax: number;
  tax_converted: number;
  converted_fee: string;
  total_amount: string;
  total_converted_amount: string;
  from_currency: Currency;
  to_currency: Currency;
  card?: {
    masked_number: string;
  };
  bank?: BankDetailsItem;
  last4?: string;
  conversion_rate?: number;
  transfer_type?: string;
  is_incoming?: boolean;
  recipient_user_id?: number;
  recipient?: {
    id: number;
    name: string;
    email: string;
  };
  organisation_name?: string;
  reason?: string;
  metadata?: TransactionMetadata | null;
}

// Helper function to map API transaction data to our Transaction interface
const mapApiTransactionToTransaction = (apiTransaction: ApiTransaction): Transaction => {
  // Format date from API (assuming ISO format) to display format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy HH:mm');
  };

  // Use the actual API status directly - no mapping needed
  const mapStatus = (apiStatus: string): TransactionStatus => {
    // Convert API status to our TransactionStatus type
    switch (apiStatus.toLowerCase()) {
      case 'declined':
        return 'declined';
      case 'pending':
        return 'pending';
      case 'failed':
        return 'failed';
      case 'completed':
        return 'completed';
      case 'reserved':
        return 'reserved';
      case 'cancelled':
        return 'cancelled';
      case 'refunded':
        return 'refunded';
      default:
        return 'completed'; // fallback to completed instead of pending
    }
  };

  return {
    id: apiTransaction.id.toString(),
    type: apiTransaction.type || 'Unknown Transaction',
    date: formatDate(apiTransaction.date_of_request),
    amount: `${formatAmount(apiTransaction.amount)} ${apiTransaction.currency}`,
    balance: apiTransaction.balance
      ? `${formatAmount(apiTransaction.balance)} ${apiTransaction.currency}`
      : `-`, // API doesn't provide running balance, using '-' as fallback
    status: mapStatus(apiTransaction.status),
    wallet_id: apiTransaction.wallet_id,
    currency_id: apiTransaction.currency_id,
    reason: apiTransaction.reason ?? apiTransaction.description ?? '-',
    // Additional API fields
    numericId: apiTransaction.id,
    currency: apiTransaction.currency,
    from_currency: apiTransaction.from_currency.code,
    to_currency: apiTransaction.to_currency.code,
    to_currency_id: apiTransaction.to_currency.id,
    created_at: apiTransaction.date_of_request,
    description: apiTransaction.description,
    note: apiTransaction.note ?? null,
    provider: apiTransaction.provider,
    fees: apiTransaction.fees,
    tax: apiTransaction.tax,
    tax_converted: apiTransaction.tax_converted,
    user_name: apiTransaction.user_name,
    user_email: apiTransaction.user_email,
    user_id: apiTransaction.user_id,
    numericAmount: apiTransaction.amount,
    converted_amount: apiTransaction.converted_amount,
    total_converted_amount: apiTransaction.total_converted_amount,
    // New fields from actual API
    reference: apiTransaction.reference,
    date_of_request: apiTransaction.date_of_request,
    date_of_payment: apiTransaction.date_of_payment,
    date_of_declined: apiTransaction.date_of_declined,
    date_of_rejection: apiTransaction.date_of_rejection,
    bank_account: apiTransaction?.bank?.iban || apiTransaction?.bank?.account_number,
    bank_name: apiTransaction?.bank?.name,
    bank_holder_name: apiTransaction?.bank?.holder,
    cardNumber: apiTransaction?.card?.masked_number,
    epsCardNumber: apiTransaction?.last4 ? `**** **** **** ${apiTransaction.last4}` : undefined,
    conversion_rate: apiTransaction.conversion_rate,
    transfer_type: apiTransaction.transfer_type,
    is_incoming: apiTransaction.is_incoming,
    recipient_user_id: apiTransaction.recipient_user_id,
    recipient: apiTransaction.recipient,
    organisation_name: apiTransaction.organisation_name,
    metadata: apiTransaction.metadata ?? null,
  };
};

const getStatusIcon = (status: TransactionStatus) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'pending':
      return <Clock className="text-grey-600 h-5 w-5" />;
    case 'failed':
      return <CircleAlert className="h-5 w-5 text-red-500" />;
    case 'declined':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'cancelled':
      return <CircleMinus className="h-5 w-5 text-red-500" />;
    case 'reserved':
      return <Clock className="h-5 w-5 text-blue-500" />;
    case 'refunded':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'queued':
      return <Clock className="h-5 w-5 text-orange-500" />;
    default:
      return <Clock className="text-grey-500 h-5 w-5" />;
  }
};

const getStatusBadgeVariant = (status: TransactionStatus) => {
  switch (status) {
    case 'completed':
      return 'green';
    case 'pending':
      return 'dark-grey';
    case 'failed':
      return 'red';
    case 'declined':
      return 'red';
    case 'cancelled':
      return 'red';
    case 'reserved':
      return 'light-blue-2';
    case 'refunded':
      return 'green';
    case 'queued':
      return 'orange';
    default:
      return 'light-grey';
  }
};

function getStatusTextColorClass(status: TransactionStatus): string {
  switch (status) {
    case 'completed':
    case 'refunded':
      return 'text-green-500 dark:text-green-400';
    case 'pending':
      return 'text-grey-600 dark:text-grey-400';
    case 'failed':
    case 'declined':
    case 'cancelled':
      return 'text-red-500 dark:text-red-400';
    case 'reserved':
      return 'text-blue-500 dark:text-blue-400';
    case 'queued':
      return 'text-orange-500 dark:text-orange-400';
    default:
      return 'text-grey-600 dark:text-grey-400';
  }
}

function renderTransactionTypeCell(
  transaction: Transaction,
  people: PersonResponse[] | undefined,
  isEmployer: boolean,
  earningSourcesById?: Map<number, GigWorkerEarningSource>
) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="bg-grey-200 dark:bg-grey-800 flex size-9 shrink-0 items-center justify-center rounded-full">
        {getTransactionIcon(transaction, earningSourcesById)}
      </div>
      <TruncateWithTooltip className="max-w-full min-w-0 text-sm font-normal text-neutral-200 dark:text-neutral-100">
        {getTransactionTypeDisplay(
          transaction.type,
          transaction.provider,
          transaction.bank_holder_name,
          transaction.transfer_type,
          transaction.recipient_user_id,
          people,
          transaction.is_incoming,
          transaction.description,
          transaction.note,
          transaction.organisation_name,
          transaction.reason,
          transaction.recipient?.name,
          isEmployer
        )}
      </TruncateWithTooltip>
    </div>
  );
}

function renderTransactionAmountCell(transaction: Transaction) {
  const isNegative = isNegativeTransactionType(
    transaction.type,
    transaction.currency_id,
    transaction.to_currency_id,
    transaction.transfer_type,
    transaction.is_incoming
  );

  return (
    <p
      className={cn(
        'text-grey-1000 text-left text-sm font-normal dark:text-neutral-100',
        !isNegative && 'font-medium'
      )}
    >
      {isNegative ? '-' : '+'}
      {transaction.amount.replace(/^[+-]/, '')}
    </p>
  );
}

function renderTransactionStatusContent(transaction: Transaction) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {getStatusIcon(transaction.status)}
      <p
        className={cn(
          'text-sm font-medium capitalize',
          getStatusTextColorClass(transaction.status)
        )}
      >
        {transaction.status}
      </p>
    </div>
  );
}

function renderTransactionActionsCell(
  transaction: Transaction,
  showDetails: (transaction: Transaction) => void
) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="shrink-0 text-sm font-medium"
      onClick={() => showDetails(transaction)}
    >
      View details
    </Button>
  );
}

function appendRecipientName(label: string, recipientName?: string): string {
  const trimmed = recipientName?.trim();
  if (!trimmed) return label;
  if (label.includes(trimmed)) return label;
  return `${label} - ${trimmed}`;
}

function shouldAppendRecipientNameForEmployer(isEmployer: boolean, transferType?: string): boolean {
  if (!isEmployer || !transferType) return false;
  return EMPLOYER_EMPLOYEE_RELATED_TRANSFER_TYPES.has(transferType.toLowerCase());
}

function withRecipientNameIfApplicable(
  label: string,
  options: {
    isEmployer: boolean;
    transferType?: string;
    recipientName?: string;
  }
): string {
  const { isEmployer, transferType, recipientName } = options;
  if (!shouldAppendRecipientNameForEmployer(isEmployer, transferType)) {
    return label;
  }
  return appendRecipientName(label, recipientName);
}

const getTransactionTypeDisplay = (
  type: string,
  provider: string,
  bankHolderName?: string,
  transferType?: string,
  recipientUserId?: number,
  people?: PersonResponse[],
  isIncoming?: boolean,
  description?: string,
  note?: string | null,
  organisationName?: string,
  reason?: string,
  recipientName?: string,
  isEmployer = false
): string => {
  let label: string;

  const formatWithRecipient = (transferTypeForCheck?: string) =>
    withRecipientNameIfApplicable(label, {
      isEmployer,
      transferType: transferTypeForCheck,
      recipientName,
    });

  // Priority 1: Use transfer_type for meaningful user-facing label
  if (transferType) {
    const lowerTransferType = transferType.toLowerCase();

    if (lowerTransferType === 'other') {
      label = reason?.trim() ?? '-';
      return formatWithRecipient(lowerTransferType);
    }

    if (lowerTransferType === 'batch-transfer') {
      label = 'Batch transfer';
      return formatWithRecipient(lowerTransferType);
    }

    // Special cases with dynamic content
    if (lowerTransferType === 'expense') {
      // Employer pays expense → "Expense"
      // Employee receives expense → "Expense reimbursement"
      label = isIncoming ? 'Expense reimbursement' : 'Expense';
      return formatWithRecipient(lowerTransferType);
    }

    if (lowerTransferType === 'team-transfer') {
      // Check if this is a batch transfer by examining the note field
      const isBatchTransfer = note?.toLowerCase().includes('batch');

      if (isBatchTransfer) {
        if (isIncoming) {
          label = organisationName ? `From ${organisationName}` : 'From team member';
        } else {
          label = 'Batch transfer';
        }
        return formatWithRecipient('batch-transfer');
      }

      // Regular team transfer (not batch)
      if (isIncoming) {
        label = description || 'From team member';
      } else if (recipientName) {
        label = `To ${recipientName}`;
      } else {
        const person = people?.find((person) => person.user_id === recipientUserId);
        label = `To ${person ? `${person?.first_name} ${person?.last_name}` : 'Team member'}`;
      }
      return formatWithRecipient(lowerTransferType);
    }

    if (lowerTransferType === 'bank-withdraw' || lowerTransferType === 'bank-withdrawal') {
      if (provider === 'card') {
        label = 'To card';
      } else {
        label = bankHolderName ? `To ${bankHolderName}` : 'Bank withdrawal';
      }
      return formatWithRecipient(lowerTransferType);
    }

    if (lowerTransferType === 'card-top-up' || lowerTransferType === 'wallet-top-up') {
      label = 'Wallet top-up';
      return formatWithRecipient(lowerTransferType);
    }

    if (lowerTransferType === 'bank-top-up' || lowerTransferType === 'bank-transfer') {
      label = 'Bank transfer';
      return formatWithRecipient(lowerTransferType);
    }

    label = TRANSFER_TYPE_LABELS[lowerTransferType] || formatTransferTypeLabel(transferType);
    return formatWithRecipient(lowerTransferType);
  }

  // Fallback for legacy or missing transfer_type - use type field
  const lowerType = type.toLowerCase();

  if (lowerType === 'rewards') {
    label = 'Balance rewards';
  } else if (lowerType === 'top-up' || lowerType === 'inbound') {
    label = 'Deposit';
  } else if (lowerType === 'withdrawal' || lowerType === 'outbound') {
    if (provider === 'card') {
      label = 'To card';
    } else if (provider === 'bank') {
      label = bankHolderName ? `To ${bankHolderName}` : 'Withdrawal';
    } else {
      label = 'Withdrawal';
    }
  } else if (lowerType === 'deposit') {
    label = 'Deposit';
  } else if (lowerType === 'wallet-to-wallet' || lowerType === 'internal') {
    label = 'Currency conversion';
  } else {
    label = type.charAt(0).toUpperCase() + type.slice(1);
  }

  return label;
};

// Helper to determine if a transaction type is negative (withdrawal) or positive
const isNegativeTransactionType = (
  type: string,
  currencyId: number,
  toCurrencyId: number,
  transferType?: string,
  isIncoming?: boolean
): boolean => {
  // Use is_incoming as the source of truth when available
  if (typeof isIncoming === 'boolean') {
    return !isIncoming;
  }

  // Fallback to type-based logic
  const lowerType = type.toLowerCase();
  switch (lowerType) {
    case 'withdrawal':
    case 'outbound':
      return true;
    case 'wallet-to-wallet':
    case 'internal':
      if (transferType === 'team-transfer') {
        return !isIncoming;
      }
      return currencyId !== toCurrencyId;
    default:
      return false;
  }
};

const getTransactionIcon = (
  transaction: Transaction,
  earningSourcesById?: Map<number, GigWorkerEarningSource>
) => {
  // Use transfer_type for icon selection when available
  const transferType = transaction.transfer_type?.toLowerCase();

  if (transferType && GIG_PAY_TRANSFER_TYPES.has(transferType)) {
    const earningSourceId = Number(transaction.metadata?.gig_platform_id);
    const earningSource = Number.isFinite(earningSourceId)
      ? earningSourcesById?.get(earningSourceId)
      : undefined;

    if (earningSource) {
      return (
        <Image
          src={getEarningSourceIconUrl(earningSource.key)}
          alt={earningSource.name}
          width={48}
          height={48}
          className="size-full rounded-full object-cover"
          unoptimized
        />
      );
    }
  }

  if (transferType) {
    switch (transferType) {
      case 'expense':
        return <ReceiptText className="text-grey-900 size-6" />;
      case 'balance-reward':
      case 'rewards':
        return <RewardIcon size={20} className="text-grey-900" />;
      case 'bank-top-up':
      case 'bank-transfer':
      case 'card-top-up':
      case 'wallet-top-up':
        return <ArrowBigUpDash className="text-grey-900 size-6" />;
      case 'bank-withdraw':
      case 'bank-withdrawal':
        return <ArrowBigDownDash className="text-grey-900 size-6" />;
      case 'currency-conversion':
      case 'team-transfer':
        return <WalletMinimal className="text-grey-900 size-6" />;
    }
  }

  // Fallback to type-based icons
  const type = transaction.type?.toLowerCase();
  switch (type) {
    case 'top-up':
    case 'inbound':
      return <ArrowBigUpDash className="text-grey-900 size-6" />;
    case 'withdrawal':
    case 'outbound':
      return <ArrowBigDownDash className="text-grey-900 size-6" />;
    case 'wallet-to-wallet':
    case 'internal':
      return <WalletMinimal className="text-grey-900 size-6" />;
    case 'rewards':
      return <RewardIcon size={20} className="text-grey-900" />;
    default:
      return <FileCog className="text-grey-900 size-6" />;
  }
};

const getMobileColumns = (
  people?: PersonResponse[],
  isEmployer = false,
  earningSourcesById?: Map<number, GigWorkerEarningSource>
): ColumnInfiniteScrollTable<Transaction>[] => {
  return [
    {
      key: 'transaction',
      render: (transaction) => (
        <div className="flex w-full items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="bg-grey-200 dark:bg-grey-800 flex size-9 shrink-0 items-center justify-center rounded-full">
              {getTransactionIcon(transaction, earningSourcesById)}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-grey-1000 truncate text-sm font-medium dark:text-neutral-100">
                {getTransactionTypeDisplay(
                  transaction.type,
                  transaction.provider,
                  transaction.bank_holder_name,
                  transaction.transfer_type,
                  transaction.recipient_user_id,
                  people,
                  transaction.is_incoming,
                  transaction.description,
                  transaction.note,
                  transaction.organisation_name,
                  transaction.reason,
                  transaction.recipient?.name,
                  isEmployer
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            <p
              className={`text-grey-1000 text-sm dark:text-neutral-100 ${
                isNegativeTransactionType(
                  transaction.type,
                  transaction.currency_id,
                  transaction.to_currency_id,
                  transaction.transfer_type,
                  transaction.is_incoming
                )
                  ? 'font-normal'
                  : 'font-medium'
              }`}
            >
              {isNegativeTransactionType(
                transaction.type,
                transaction.currency_id,
                transaction.to_currency_id,
                transaction.transfer_type,
                transaction.is_incoming
              )
                ? '-'
                : '+'}
              {transaction.amount.replace(/^[+-]/, '')}
            </p>
            <div className="flex items-center gap-1">
              {getStatusIcon(transaction.status)}
              <Badge
                variant={getStatusBadgeVariant(transaction.status)}
                text={transaction.status}
                className="text-xs capitalize"
              />
            </div>
          </div>
        </div>
      ),
    },
  ];
};

const getColumns = (
  showDetails: (transaction: Transaction) => void,
  people?: PersonResponse[],
  isEmployer = false,
  earningSourcesById?: Map<number, GigWorkerEarningSource>
): ColumnInfiniteScrollTable<Transaction>[] => {
  return [
    {
      key: 'type',
      header: 'TRANSACTION',
      size: 80,
      render: (transaction) =>
        renderTransactionTypeCell(transaction, people, isEmployer, earningSourcesById),
    },
    {
      key: 'amount',
      header: 'AMOUNT',
      size: 36,
      render: (transaction) => renderTransactionAmountCell(transaction),
    },
    {
      key: 'balance',
      header: 'BALANCE',
      size: 40,
      render: (transaction) => (
        <p className="text-grey-1000 text-left text-sm font-normal dark:text-neutral-100">
          {transaction.balance}
        </p>
      ),
    },
    {
      key: 'status',
      header: 'STATUS',
      size: 36,
      render: (transaction) => renderTransactionStatusContent(transaction),
    },
    {
      key: 'actions',
      header: '',
      size: 28,
      render: (transaction) => renderTransactionActionsCell(transaction, showDetails),
    },
  ];
};

const getTabletColumns = (
  showDetails: (transaction: Transaction) => void,
  people?: PersonResponse[],
  isEmployer = false,
  earningSourcesById?: Map<number, GigWorkerEarningSource>
): ColumnInfiniteScrollTable<Transaction>[] => {
  return [
    {
      key: 'type',
      header: 'TRANSACTION',
      size: 50,
      render: (transaction) =>
        renderTransactionTypeCell(transaction, people, isEmployer, earningSourcesById),
    },
    {
      key: 'amount',
      header: 'AMOUNT',
      size: 25,
      render: (transaction) => renderTransactionAmountCell(transaction),
    },
    {
      key: 'status',
      header: 'STATUS',
      size: 25,
      render: (transaction) => renderTransactionStatusContent(transaction),
    },
    {
      key: 'actions',
      header: '',
      size: 25,
      render: (transaction) => renderTransactionActionsCell(transaction, showDetails),
    },
  ];
};

const transformPeopleToOptions = (response: ApiResponse): ApiComboboxOption[] => {
  const data = (response.data as PersonResponse[] | undefined) ?? [];
  return data.map((person) => {
    const fullName = [person.first_name?.trim(), person.last_name?.trim()]
      .filter(Boolean)
      .join(' ');
    return {
      value: String(person.user_id),
      name: fullName || person.email || `User ${person.user_id}`,
    };
  });
};

const formSchema = z.object({
  transfer_types: z.array(z.string()).optional(),
  team_members: z.array(z.string()).optional(),
  sources: z.array(z.string()).optional(),
  statuses: z.array(z.string()).optional(),
  currency: z.string().optional(),
  search: z.string().optional(),
  date_range: z
    .object({
      from: z.date(),
      to: z.date(),
    })
    .optional(),
});

interface FiltersProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  isEmployer: boolean;
  organizationId: number;
  onTeamMembersChange: (options: ApiComboboxOption[]) => void;
  transferTypeOptions: { value: string; name: string }[];
  isTransferTypesLoading: boolean;
  sourceOptions: {
    value: string;
    name: string;
    image: {
      path: string;
      alt: string;
    };
  }[];
  isSourceOptionsLoading: boolean;
  showSourceFilter: boolean;
  currencyOptions: ComboboxOption[];
  showCurrencyFilter: boolean;
}

const SearchField = ({ form }: { form: UseFormReturn<z.infer<typeof formSchema>> }) => {
  return (
    <FormField
      control={form.control}
      name="search"
      render={({ field }) => (
        <FormItem className="w-full">
          <div className="relative">
            <FormControl>
              <Input
                size="sm"
                {...field}
                capitalizeFirstLetter={false}
                placeholder=""
                value={field.value ?? ''}
                preventEnterSubmit
                startIcon={<Search />}
              />
            </FormControl>
          </div>
        </FormItem>
      )}
    />
  );
};

const Filters = ({
  form,
  isEmployer,
  organizationId,
  onTeamMembersChange,
  transferTypeOptions,
  isTransferTypesLoading,
  sourceOptions,
  isSourceOptionsLoading,
  showSourceFilter,
  currencyOptions,
  showCurrencyFilter,
}: FiltersProps) => {
  const teamMemberParams = useMemo(
    () => ({
      organisation_id: String(organizationId),
      roles: [
        USER_ROLES.EMPLOYEE,
        USER_ROLES.PAYROLL_SEAT,
        USER_ROLES.INDIVIDUAL_CONTRACTOR,
        USER_ROLES.COMPANY_CONTRACTOR,
        USER_ROLES.CONTRACTOR_OF_RECORD,
        USER_ROLES.GIG_WORKER,
      ],
    }),
    [organizationId]
  );

  const statusOptions = [
    { value: 'pending', name: 'Pending' },
    { value: 'reserved', name: 'Reserved' },
    { value: 'completed', name: 'Completed' },
    { value: 'refunded', name: 'Refunded' },
    { value: 'failed', name: 'Failed' },
    { value: 'declined', name: 'Declined' },
    { value: 'cancelled', name: 'Cancelled' },
  ];

  return (
    <>
      <FormField
        control={form.control}
        name="transfer_types"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Combobox
                multiple
                placeholder="Transfer type"
                onChange={field.onChange}
                options={transferTypeOptions}
                value={field.value || []}
                clearAllButton
                showListValues={false}
                showFilter={true}
                size="sm"
                disabled={isTransferTypesLoading}
                selectionName="Transfer type"
              />
            </FormControl>
          </FormItem>
        )}
      />
      {showCurrencyFilter ? (
        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Combobox
                  placeholder="Currency"
                  onChange={field.onChange}
                  options={currencyOptions}
                  value={field.value || ''}
                  showFilter={true}
                  showClearButton
                  size="sm"
                />
              </FormControl>
            </FormItem>
          )}
        />
      ) : null}
      {isEmployer && !isDefaultOrganisation(organizationId) ? (
        <FormField
          control={form.control}
          name="team_members"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ApiCombobox
                  multiple
                  placeholder="Team member"
                  onChange={(value, options) => {
                    field.onChange(value);
                    onTeamMembersChange(options ?? []);
                  }}
                  value={field.value || []}
                  clearAllButton
                  showListValues={false}
                  showFilter={true}
                  size="sm"
                  selectionName="Team member"
                  url={`${process.env.NEXT_PUBLIC_EOR_API_URL}/people`}
                  fetchFunction={fetchAPI}
                  additionalParams={{ ...teamMemberParams, unique_users: true }}
                  transformResponse={transformPeopleToOptions}
                />
              </FormControl>
            </FormItem>
          )}
        />
      ) : null}
      <FormField
        control={form.control}
        name="statuses"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Combobox
                multiple
                placeholder="Status"
                onChange={field.onChange}
                options={statusOptions}
                value={field.value || []}
                clearAllButton
                showListValues={false}
                size="sm"
                selectionName="Status"
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="date_range"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <DateRangePicker
                placeholder="Date"
                value={field.value}
                onChange={field.onChange}
                clearable={true}
                size="sm"
              />
            </FormControl>
          </FormItem>
        )}
      />
      {showSourceFilter ? (
        <FormField
          control={form.control}
          name="sources"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Combobox
                  multiple
                  placeholder="Sources"
                  onChange={field.onChange}
                  options={sourceOptions}
                  value={field.value || []}
                  clearAllButton
                  showListValues={false}
                  showFilter={true}
                  size="sm"
                  disabled={isSourceOptionsLoading}
                  selectionName="Sources"
                />
              </FormControl>
            </FormItem>
          )}
        />
      ) : null}
    </>
  );
};

interface AppliedTransactionFilters {
  transaction_types: string[];
  team_members: string[];
  sources: string[];
  statuses: string[];
  currency: string;
  date_range: {
    from?: Date | null | undefined;
    to?: Date | null | undefined;
  };
}

interface TransactionsTableProps {
  walletId?: number;
  /**
   * Currencies the user holds. When provided the table spans every currency
   * instead of a single wallet, and the Currency filter is shown.
   */
  currencies?: Currency[];
  onViewDetails?: (transaction: Transaction) => void;
}

export function TransactionsTable({ walletId, currencies, onViewDetails }: TransactionsTableProps) {
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);

  // Filters/search only — infinite scroll keeps the cursor internal, so this table
  // uses useQueryStates directly instead of useNuqsTableState (no page in the URL).
  const [urlState, setUrlState] = useQueryStates(transactionsTableParsers, {
    history: 'replace',
  });

  const appliedFilters: AppliedTransactionFilters = useMemo(
    () => ({
      transaction_types: urlState.transactionsTransferTypes,
      team_members: urlState.transactionsTeamMembers,
      sources: urlState.transactionsSources,
      statuses: urlState.transactionsStatuses,
      currency: urlState.transactionsCurrency,
      date_range: {
        from: urlState.transactionsDateFrom ?? undefined,
        to: urlState.transactionsDateTo ?? undefined,
      },
    }),
    [
      urlState.transactionsTransferTypes,
      urlState.transactionsTeamMembers,
      urlState.transactionsSources,
      urlState.transactionsStatuses,
      urlState.transactionsCurrency,
      urlState.transactionsDateFrom,
      urlState.transactionsDateTo,
    ]
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      transfer_types: urlState.transactionsTransferTypes,
      team_members: urlState.transactionsTeamMembers,
      sources: urlState.transactionsSources,
      statuses: urlState.transactionsStatuses,
      currency: urlState.transactionsCurrency,
      search: urlState.transactionsSearch,
      date_range:
        urlState.transactionsDateFrom && urlState.transactionsDateTo
          ? { from: urlState.transactionsDateFrom, to: urlState.transactionsDateTo }
          : undefined,
    },
  });

  const isContractor = useIsContractor();
  const isGigWorker = useIsGigWorker();
  const isEmployee = useIsEmployee();

  // Fetch transfer types from API
  const { data: transferTypesData, isLoading: transferTypesLoading } = useTransferTypes();

  // Convert transfer types to options for the Combobox
  const transferTypeOptions = useMemo(() => {
    if (!transferTypesData) return [];
    return Object.values(transferTypesData)
      .filter((value) => {
        const lower = value.toLowerCase();
        if (isEmployee) {
          return EMPLOYEE_WALLET_TRANSFER_TYPES.has(lower);
        }
        if (isContractor) {
          return !GIG_PAY_TRANSFER_TYPES.has(lower);
        }
        return value !== 'contractor-payment-bank-withdraw';
      })
      .map((kebabValue) => ({
        value: kebabValue,
        name: TRANSFER_TYPE_LABELS[kebabValue] || formatTransferTypeLabel(kebabValue),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [transferTypesData, isContractor, isEmployee]);

  const formSearch = form.watch('search') ?? '';
  const [debouncedSearch] = useDebounce(formSearch, 300);

  useEffect(() => {
    if (form.getValues('search') === urlState.transactionsSearch) return;
    form.setValue('search', urlState.transactionsSearch);
  }, [form, urlState.transactionsSearch]);

  useEffect(() => {
    if (debouncedSearch !== formSearch) return;
    if (debouncedSearch === urlState.transactionsSearch) return;

    void setUrlState({
      transactionsSearch: debouncedSearch || null,
    });
  }, [debouncedSearch, formSearch, setUrlState, urlState.transactionsSearch]);

  const { isAuthenticated, accessToken } = useAuth();
  const { data: userVerification } = useUserVerification(isAuthenticated && !!accessToken);

  const isEmployer = useMemo(() => {
    return userVerification?.roles?.includes('employer') ?? false;
  }, [userVerification]);

  const showSourceFilter = isGigWorker;
  const shouldFetchEarningSources = isGigWorker || isEmployer;

  // With currencies supplied the table spans every wallet, so it no longer waits on a wallet id.
  const showCurrencyFilter = !!currencies?.length;
  const isScopedToWallet = !showCurrencyFilter;

  const currencyOptions = useMemo<ComboboxOption[]>(
    () =>
      (currencies ?? []).map((curr) => ({
        value: curr.code,
        name: `${curr.name} (${curr.code})`,
        image: {
          path: `https://i.nativeteams.com/assets/app/flags_code/${curr.flag_code}.svg`,
          alt: curr.code,
        },
      })),
    [currencies]
  );

  const { data: earningSourcesData, isLoading: earningSourcesLoading } = useQuery({
    queryKey: ['earning-sources'],
    queryFn: getGigWorkerEarningSources,
    enabled: shouldFetchEarningSources,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  const earningSources = earningSourcesData?.data ?? [];

  const sourceOptions = useMemo(() => {
    return earningSources
      .filter((source) => !EXCLUDED_EARNING_SOURCE_KEYS.includes(source.key))
      .map((source) => ({
        value: String(source.id),
        name: source.name,
        image: {
          path: getEarningSourceIconUrl(source.key),
          alt: source.name,
        },
      }));
  }, [earningSources]);

  const earningSourcesById = useMemo(() => {
    return new Map(earningSources.map((source) => [source.id, source]));
  }, [earningSources]);

  const organizationId =
    userVerification?.active_organisation?.id ?? userVerification?.organisations?.[0]?.id ?? 0;

  const { data: peopleData } = useQuery({
    queryKey: ['people', organizationId, { unique_users: true }],
    queryFn: () =>
      getPeople({
        organisation_id: organizationId,
        current_page: 1,
        per_page: 20,
        unique_users: true,
      }),
    enabled: isEmployer && organizationId > 0 && !isDefaultOrganisation(organizationId),
  });

  const people = peopleData?.data ?? [];

  // Maps selected team member ids to their display names (populated from the
  // ApiCombobox selection / people list) so we can label the active filter badges.
  const [teamMemberNames, setTeamMemberNames] = useState<Record<string, string>>({});

  const handleTeamMembersChange = useCallback((options: ApiComboboxOption[]) => {
    setTeamMemberNames((prev) => {
      const next = { ...prev };
      options.forEach((option) => {
        next[String(option.value)] = option.name;
      });
      return next;
    });
  }, []);

  const missingTeamMemberIds = useMemo(
    () => appliedFilters.team_members.filter((id) => !teamMemberNames[id]),
    [appliedFilters.team_members, teamMemberNames]
  );

  const { data: selectedPeopleForBadges } = useQuery({
    queryKey: ['people', 'badge-labels', organizationId, appliedFilters.team_members],
    queryFn: () =>
      getPeople({
        organisation_id: organizationId,
        user_ids: appliedFilters.team_members,
        current_page: 1,
        per_page: Math.max(appliedFilters.team_members.length, 1),
        unique_users: true,
      }),
    enabled: isEmployer && organizationId > 0 && missingTeamMemberIds.length > 0,
  });

  useEffect(() => {
    if (!appliedFilters.team_members.length) return;

    setTeamMemberNames((prev) => {
      let didChange = false;
      const next = { ...prev };

      for (const id of appliedFilters.team_members) {
        if (next[id]) continue;

        const person =
          selectedPeopleForBadges?.data?.find((entry) => String(entry.user_id) === id) ??
          people.find((entry) => String(entry.user_id) === id);

        if (!person) continue;

        const fullName = [person.first_name?.trim(), person.last_name?.trim()]
          .filter(Boolean)
          .join(' ');
        next[id] = fullName || person.email || `User ${person.user_id}`;
        didChange = true;
      }

      return didChange ? next : prev;
    });
  }, [appliedFilters.team_members, people, selectedPeopleForBadges?.data]);

  const teamMembers = form.watch('team_members');
  const sources = form.watch('sources');

  useEffect(() => {
    if (!isEmployer && teamMembers?.length) {
      form.setValue('team_members', []);
    }
  }, [isEmployer, teamMembers, form]);

  useEffect(() => {
    if (!isEmployer && appliedFilters.team_members.length > 0) {
      void setUrlState({ transactionsTeamMembers: null });
    }
  }, [isEmployer, appliedFilters.team_members.length, setUrlState]);

  useEffect(() => {
    if (!showSourceFilter && sources?.length) {
      form.setValue('sources', []);
    }
  }, [showSourceFilter, sources, form]);

  useEffect(() => {
    if (!showSourceFilter && appliedFilters.sources.length > 0) {
      void setUrlState({ transactionsSources: null });
    }
  }, [showSourceFilter, appliedFilters.sources.length, setUrlState]);

  // Build filters for the API call using appliedFilters
  const apiFilters = useMemo(() => {
    const filters: TransactionFilters = {
      per_page: clampTransactionsPageSize(urlState.transactionsPageSize),
    };

    if (isScopedToWallet) {
      filters.wallet_id = walletId;
    }

    if (showCurrencyFilter && appliedFilters.currency) {
      filters.currency = appliedFilters.currency;
    }

    if (urlState.transactionsSearch) {
      filters.search = urlState.transactionsSearch;
    }

    // Convert our status filter to API format
    if (appliedFilters.statuses && appliedFilters.statuses.length > 0) {
      // Check if API supports multiple statuses via 'statuses' parameter
      type ApiTransactionStatus = Exclude<TransactionStatus, 'queued'>;
      const validStatuses = appliedFilters.statuses.filter(
        (status): status is ApiTransactionStatus =>
          status === 'pending' ||
          status === 'failed' ||
          status === 'declined' ||
          status === 'completed' ||
          status === 'reserved' ||
          status === 'cancelled' ||
          status === 'refunded'
      );

      if (validStatuses.length === 1) {
        // Single status - use 'status' parameter
        filters.status = validStatuses[0];
      } else if (validStatuses.length > 1) {
        // Multiple statuses - use 'status[]' array parameter
        filters.statuses = validStatuses;
      }
    }

    // Convert our transfer type filter to API format
    if (appliedFilters.transaction_types && appliedFilters.transaction_types.length > 0) {
      if (appliedFilters.transaction_types.length === 1) {
        // Single transfer type - use 'transfer_type' parameter
        filters.transfer_type = appliedFilters.transaction_types[0];
      } else {
        // Multiple transfer types - use 'transfer_types' array parameter
        filters.transfer_types = appliedFilters.transaction_types;
      }
    }

    // Add team member filter
    if (appliedFilters.team_members && appliedFilters.team_members.length > 0) {
      // Convert string array to number array for API
      const teamMemberIds = appliedFilters.team_members
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));

      if (teamMemberIds.length > 0) {
        filters.team_member_ids = teamMemberIds;
      }
    }

    if (showSourceFilter && appliedFilters.sources && appliedFilters.sources.length > 0) {
      const sourceIds = appliedFilters.sources
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));

      if (sourceIds.length > 0) {
        filters.gig_platform_ids = sourceIds;
      }
    }

    // Add date range filters
    if (appliedFilters.date_range?.from) {
      filters.date_from = format(appliedFilters.date_range.from, 'yyyy-MM-dd');
    }
    if (appliedFilters.date_range?.to) {
      filters.date_to = format(appliedFilters.date_range.to, 'yyyy-MM-dd');
    }

    return filters;
  }, [
    walletId,
    isScopedToWallet,
    showCurrencyFilter,
    urlState.transactionsSearch,
    urlState.transactionsPageSize,
    appliedFilters,
    showSourceFilter,
  ]);

  const isQueryEnabled = isScopedToWallet ? !!walletId : true;

  const transactionsQuery = useInfiniteTransactions(
    apiFilters,
    isQueryEnabled,
    mapApiTransactionToTransaction
  );

  const mapTransactionTableRows = useCallback((pages: TransactionsInfinitePage[]) => {
    return pages.flatMap((page) => page.data);
  }, []);

  const getGroupLabel = useCallback(
    (transaction: Transaction, previousTransaction: Transaction | null) =>
      getTransactionGroupLabel(transaction, previousTransaction),
    []
  );

  const handleViewDetails = useCallback(
    (transaction: Transaction) => {
      onViewDetails?.(transaction);
    },
    [onViewDetails]
  );

  const columns = useMemo(
    () => getColumns(handleViewDetails, people, isEmployer, earningSourcesById),
    [handleViewDetails, people, isEmployer, earningSourcesById]
  );

  const tabletColumns = useMemo(
    () => getTabletColumns(handleViewDetails, people, isEmployer, earningSourcesById),
    [handleViewDetails, people, isEmployer, earningSourcesById]
  );

  const mobileColumns = useMemo(
    () => getMobileColumns(people, isEmployer, earningSourcesById),
    [people, isEmployer, earningSourcesById]
  );

  const handleApplyFilters = useCallback(() => {
    const formValues = form.getValues();
    const transferTypes = formValues.transfer_types || [];
    const teamMembersValue = formValues.team_members || [];
    const sourcesValue = showSourceFilter ? formValues.sources || [] : [];
    const statuses = formValues.statuses || [];
    const currencyValue = showCurrencyFilter ? formValues.currency || '' : '';

    void setUrlState({
      transactionsTransferTypes: transferTypes.length ? transferTypes : null,
      transactionsTeamMembers: teamMembersValue.length ? teamMembersValue : null,
      transactionsSources: sourcesValue.length ? sourcesValue : null,
      transactionsStatuses: statuses.length ? statuses : null,
      transactionsCurrency: currencyValue || null,
      transactionsDateFrom: formValues.date_range?.from ?? null,
      transactionsDateTo: formValues.date_range?.to ?? null,
    });
  }, [form, setUrlState, showSourceFilter, showCurrencyFilter]);

  const handleClearFiltersInPopover = useCallback(() => {
    form.setValue('transfer_types', [], { shouldDirty: true });
    form.setValue('team_members', [], { shouldDirty: true });
    form.setValue('sources', [], { shouldDirty: true });
    form.setValue('statuses', [], { shouldDirty: true });
    form.setValue('currency', '', { shouldDirty: true });
    form.setValue('date_range', undefined, { shouldDirty: true });

    void setUrlState({
      transactionsTransferTypes: null,
      transactionsTeamMembers: null,
      transactionsSources: null,
      transactionsStatuses: null,
      transactionsCurrency: null,
      transactionsDateFrom: null,
      transactionsDateTo: null,
    });
  }, [form, setUrlState]);

  const handleClearAllFilters = useCallback(() => {
    const currentSearch = form.getValues('search');
    form.reset({
      transfer_types: [],
      team_members: [],
      sources: [],
      statuses: [],
      currency: '',
      search: currentSearch || '',
      date_range: undefined,
    });

    void setUrlState({
      transactionsTransferTypes: null,
      transactionsTeamMembers: null,
      transactionsSources: null,
      transactionsStatuses: null,
      transactionsCurrency: null,
      transactionsDateFrom: null,
      transactionsDateTo: null,
    });
  }, [form, setUrlState]);

  const handleFiltersOpenChange = useCallback(
    (open: boolean) => {
      setAreFiltersOpen(open);
      if (open) {
        form.setValue('transfer_types', appliedFilters.transaction_types);
        form.setValue('team_members', appliedFilters.team_members);
        form.setValue('sources', showSourceFilter ? appliedFilters.sources : []);
        form.setValue('statuses', appliedFilters.statuses);
        form.setValue('currency', showCurrencyFilter ? appliedFilters.currency : '');
        // Only set date_range if both from and to are defined (form schema requires both)
        if (appliedFilters.date_range?.from && appliedFilters.date_range?.to) {
          form.setValue('date_range', {
            from: appliedFilters.date_range.from,
            to: appliedFilters.date_range.to,
          });
        } else {
          form.setValue('date_range', undefined);
        }
      }
    },
    [form, appliedFilters, showSourceFilter, showCurrencyFilter]
  );

  const getStatusLabel = useCallback((statusValue: string) => {
    const statusOptions = [
      { value: 'pending', name: 'Pending' },
      { value: 'reserved', name: 'Reserved' },
      { value: 'completed', name: 'Completed' },
      { value: 'refunded', name: 'Refunded' },
      { value: 'failed', name: 'Failed' },
      { value: 'declined', name: 'Declined' },
      { value: 'cancelled', name: 'Cancelled' },
    ];
    return statusOptions.find((s) => s.value === statusValue)?.name || statusValue;
  }, []);

  const getTeamMemberNames = useCallback(
    (teamMemberIds: string[]) => {
      return teamMemberIds
        .map((id) => teamMemberNames[id] || id)
        .filter(Boolean)
        .join(', ');
    },
    [teamMemberNames]
  );

  const getSourceNames = useCallback(
    (sourceIds: string[]) => {
      return sourceIds
        .map((id) => sourceOptions.find((source) => source.value === id)?.name || id)
        .filter(Boolean)
        .join(', ');
    },
    [sourceOptions]
  );

  const getDateRangeLabel = useCallback(() => {
    const { from, to } = appliedFilters.date_range;
    if (!from && !to) return '';
    if (from && to) {
      return `${format(from, 'dd/MM/yyyy')} - ${format(to, 'dd/MM/yyyy')}`;
    }
    if (from) return `From ${format(from, 'dd/MM/yyyy')}`;
    if (to) return `To ${format(to, 'dd/MM/yyyy')}`;
    return '';
  }, [appliedFilters.date_range]);

  const activeFilterBadges = useMemo(() => {
    const badges: Array<{
      key: string;
      label: string;
      value: string | string[];
      onClear: () => void;
    }> = [];

    if (appliedFilters.transaction_types.length > 0) {
      badges.push({
        key: 'transfer_types',
        label: 'Transfer type',
        value: appliedFilters.transaction_types
          .map((type) => TRANSFER_TYPE_LABELS[type] || formatTransferTypeLabel(type))
          .join(', '),
        onClear: () => {
          form.setValue('transfer_types', []);
          void setUrlState({ transactionsTransferTypes: null });
        },
      });
    }

    if (appliedFilters.team_members.length > 0) {
      badges.push({
        key: 'team_members',
        label: 'Team member',
        value: getTeamMemberNames(appliedFilters.team_members),
        onClear: () => {
          form.setValue('team_members', []);
          void setUrlState({ transactionsTeamMembers: null });
        },
      });
    }

    if (showSourceFilter && appliedFilters.sources.length > 0) {
      badges.push({
        key: 'sources',
        label: 'Source',
        value: getSourceNames(appliedFilters.sources),
        onClear: () => {
          form.setValue('sources', []);
          void setUrlState({ transactionsSources: null });
        },
      });
    }

    if (appliedFilters.statuses.length > 0) {
      badges.push({
        key: 'statuses',
        label: 'Status',
        value: appliedFilters.statuses.map(getStatusLabel).join(', '),
        onClear: () => {
          form.setValue('statuses', []);
          void setUrlState({ transactionsStatuses: null });
        },
      });
    }

    if (showCurrencyFilter && appliedFilters.currency) {
      badges.push({
        key: 'currency',
        label: 'Currency',
        value: appliedFilters.currency,
        onClear: () => {
          form.setValue('currency', '');
          void setUrlState({ transactionsCurrency: null });
        },
      });
    }

    if (appliedFilters.date_range?.from || appliedFilters.date_range?.to) {
      badges.push({
        key: 'date_range',
        label: 'Date',
        value: getDateRangeLabel(),
        onClear: () => {
          form.setValue('date_range', undefined);
          void setUrlState({
            transactionsDateFrom: null,
            transactionsDateTo: null,
          });
        },
      });
    }

    return badges;
  }, [
    appliedFilters,
    getStatusLabel,
    getTeamMemberNames,
    getSourceNames,
    getDateRangeLabel,
    form,
    setUrlState,
    showSourceFilter,
    showCurrencyFilter,
  ]);

  if (transactionsQuery.error) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-red-600 dark:text-red-400">
          Error loading transactions: {transactionsQuery.error.message}
        </p>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form>
        <div className="bg-grey-25 dark:bg-grey-1000 grid w-full grid-cols-1 items-end gap-5 rounded-4xl p-8 shadow-[0px_429px_120px_rgba(42,43,65,0),0px_17px_38px_rgba(42,43,65,0.05)]">
          <div className="border-b-grey-300 dark:border-b-grey-800 flex w-full flex-col items-center gap-5 border-b pb-5 align-middle">
            <div className="flex w-full flex-col items-start justify-between align-middle sm:flex-row sm:items-center">
              <p className="text-base leading-normal font-medium text-neutral-200 sm:text-lg dark:text-neutral-100">
                Latest transactions
              </p>
            </div>

            <div className="flex w-full flex-col gap-3">
              <div className="flex w-full flex-col justify-between gap-6 md:flex-row md:gap-4">
                <div className="w-full md:grow">
                  <SearchField form={form} />
                </div>

                <Popover open={areFiltersOpen} onOpenChange={handleFiltersOpenChange}>
                  <PopoverTrigger asChild>
                    <Button
                      disabled={!isQueryEnabled}
                      size="sm"
                      variant="secondary"
                      className="w-full text-base md:w-[132px] md:min-w-[132px] md:justify-center md:text-sm"
                    >
                      <ListFilter className="h-4! w-4! stroke-2! md:h-4! md:w-4!" />
                      Filters
                      {activeFilterBadges.length > 0 && (
                        <div className="flex h-4 w-4 min-w-4 items-center justify-center rounded-full bg-black text-xs font-medium leading-none text-white dark:bg-white dark:text-black">
                          {activeFilterBadges.length}
                        </div>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    avoidCollisions={false}
                    className="grid w-full grid-cols-1 gap-4 shadow-lg md:w-[573px] md:grid-cols-2"
                  >
                    <AvoidCollisionsContext.Provider value={false}>
                      <Filters
                        form={form}
                        isEmployer={isEmployer}
                        organizationId={organizationId}
                        onTeamMembersChange={handleTeamMembersChange}
                        transferTypeOptions={transferTypeOptions}
                        isTransferTypesLoading={transferTypesLoading}
                        sourceOptions={sourceOptions}
                        isSourceOptionsLoading={earningSourcesLoading}
                        showSourceFilter={showSourceFilter}
                        currencyOptions={currencyOptions}
                        showCurrencyFilter={showCurrencyFilter}
                      />
                    </AvoidCollisionsContext.Provider>

                    <div className="col-span-1 mt-4 flex justify-end gap-2 md:col-span-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-[134px]"
                        onClick={(e) => {
                          e.preventDefault();
                          handleClearFiltersInPopover();
                          setAreFiltersOpen(false);
                        }}
                      >
                        Clear filters
                      </Button>

                      <Button
                        size="sm"
                        variant="primary"
                        className="w-[134px]"
                        onClick={(e) => {
                          e.preventDefault();
                          handleApplyFilters();
                          setAreFiltersOpen(false);
                        }}
                      >
                        Apply filters
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {activeFilterBadges.length > 0 && (
                <div className="flex w-full flex-wrap items-center gap-2">
                  {activeFilterBadges.map((filter) => (
                    <Badge
                      key={filter.key}
                      variant="filled-grey"
                      size="m"
                      iconAfter={
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            filter.onClear();
                          }}
                          className="text-grey-1100 dark:text-neutral-200"
                          aria-label={`Clear ${filter.label} filter`}
                        >
                          <X className="h-3! w-3!" />
                        </Button>
                      }
                      className="cursor-default"
                    >
                      <span className="text-grey-1100 font-normal dark:text-neutral-200">
                        {filter.label}: <span className="font-medium">{filter.value}</span>
                      </span>
                    </Badge>
                  ))}

                  {activeFilterBadges.length > 1 && (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={(e) => {
                        e.preventDefault();
                        handleClearAllFilters();
                      }}
                      className="dark:text-grey-400 dark:hover:text-grey-200 h-auto px-2 py-1 text-sm font-medium text-neutral-200"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <GroupedInfiniteScrollTable<Transaction, TransactionsInfinitePage>
            columns={columns}
            mobileColumns={mobileColumns}
            tabletColumns={tabletColumns}
            query={transactionsQuery}
            mapFlatData={mapTransactionTableRows}
            getGroupLabel={getGroupLabel}
            getItemKey={(transaction) => transaction.id}
            renderGroupLabel={(dateLabel) => <TransactionDateGroupHeader dateLabel={dateLabel} />}
            tableHeaderWrapperClassName="mb-5 bg-transparent dark:bg-transparent"
          />
        </div>
      </form>
    </Form>
  );
}
